import React from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Building2, Users, Eye, UserPlus, Edit, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const landingBaseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AdminCommitteesTab = ({
  committees,
  openCommitteeDialog,
  setCommitteeMemberLimits,
  setCommitteeInviteForm,
  setCommitteeInviteDialog,
  deleteCommittee,
  removeCommitteeMember,
  getRoleDisplay
}) => {
  const navigate = useNavigate();

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Manage Committees</h2>
        <Button
          onClick={() => openCommitteeDialog()}
          className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm"
          data-testid="create-committee-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Committee
        </Button>
      </div>

      {committees.length === 0 ? (
        <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Committees Yet</h3>
          <p className="text-slate-500 mb-4">Create your first committee to get started</p>
          <Button
            onClick={() => openCommitteeDialog()}
            className="bg-greek-blue hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Committee
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {committees.map((committee) => (
            <div key={committee.id} className="bg-white rounded-sm border border-slate-200 p-6" data-testid={`committee-row-${committee.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-greek-blue/10 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-greek-blue" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-lg font-semibold text-slate-900">{committee.name}</h3>
                      {committee.level && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${
                          committee.level === 'national' ? 'bg-amber-100 text-amber-800' :
                          committee.level === 'district' ? 'bg-blue-100 text-blue-800' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {committee.level}
                        </span>
                      )}
                    </div>
                    <p className="text-slate-500 text-sm">{committee.description || 'No description'}</p>
                    {committee.slug && (
                      <div className="mt-2 text-sm text-slate-600">
                        Landing Page:{' '}
                        <a
                          href={`${landingBaseUrl}/${committee.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-greek-blue hover:underline"
                          data-testid={`committee-landing-link-${committee.id}`}
                        >
                          {landingBaseUrl}/{committee.slug}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {committee.members?.length || 0} members
                      </span>
                      <span>Created: {new Date(committee.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/committee-dashboard?committee=${committee.id}`)}
                    className="rounded-sm bg-greek-blue/5 border-greek-blue/20 text-greek-blue hover:bg-greek-blue/10"
                    data-testid={`view-committee-${committee.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Dashboard
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/committees/${committee.id}/member-limits`);
                        setCommitteeMemberLimits(response.data);
                      } catch (error) {
                        console.error('Failed to fetch member limits:', error);
                        setCommitteeMemberLimits(null);
                      }
                      setCommitteeInviteForm({ email: '', first_name: '', last_name: '', role: 'chair' });
                      setCommitteeInviteDialog({ open: true, committee });
                    }}
                    className="rounded-sm"
                    data-testid={`invite-to-committee-${committee.id}`}
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Invite
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openCommitteeDialog(committee)}
                    className="rounded-sm"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteCommittee(committee.id, committee.name)}
                    className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`delete-committee-${committee.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
              
              {/* Committee Members */}
              {committee.members && committee.members.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Members ({committee.members.length})</h4>
                  <div className="space-y-2">
                    {committee.members.map((member, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-slate-50 rounded-sm px-3 py-2"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-greek-blue/10 rounded-full flex items-center justify-center text-greek-blue text-sm font-medium">
                            {member.first_name?.[0]}{member.last_name?.[0]}
                          </div>
                          <div>
                            <span className="font-medium text-slate-900">{member.first_name} {member.last_name}</span>
                            <p className="text-xs text-slate-500">{member.email}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            member.role === 'chair' 
                              ? 'bg-greek-blue/10 text-greek-blue'
                              : member.role === 'co_chair'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-slate-200 text-slate-600'
                          }`}>
                            {getRoleDisplay(member.role)}
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeCommitteeMember(committee.id, member.user_id, member.first_name, member.last_name)}
                          className="text-red-600 border-red-200 hover:bg-red-50 rounded-sm"
                          data-testid={`remove-member-${member.user_id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default AdminCommitteesTab;